
import { useState, useEffect, useCallback, useRef } from "react";
import { useMeta } from "@/contexts/MetaContext";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { AdItem, UseFetchTopAdsReturn } from "./types";
import { 
  fetchRealAdData, 
  getFallbackAdData, 
  getLocalStorageAdData 
} from "./adDataFetcher";
import { 
  getAdDataFromCache, 
  saveAdDataToCache, 
  cleanExpiredCache,
  REFRESH_INTERVAL 
} from "./adCacheUtils";

export const useFetchTopAds = (): UseFetchTopAdsReturn => {
  const { accessToken, isConnected, selectedCampaignId } = useMeta();
  const { user } = useAuth();
  const [topAds, setTopAds] = useState<AdItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const fetchInProgress = useRef(false);
  const mountedRef = useRef(true);
  const { toast } = useToast();
  
  // Track when a success toast was last shown
  const lastSuccessToast = useRef(0);

  const fetchTopAds = useCallback(async (force = false) => {
    // Prevent multiple fetches and check if we should fetch
    if (fetchInProgress.current || !isConnected) {
      return;
    }
    
    const now = Date.now();
    if (!force && now - lastFetchTime < REFRESH_INTERVAL && lastFetchTime !== 0) {
      console.log("Skipping top ads fetch, last fetch was recent");
      return;
    }

    fetchInProgress.current = true;
    setIsLoading(true);
    setError(null);

    try {
      console.log("Checking/fetching ad data at", new Date().toISOString());
      
      // Only proceed with authenticated users for database operations
      if (user && user.id && selectedCampaignId) {
        // First check if we have valid cached data in Supabase
        if (!force) {
          const cachedResult = await getAdDataFromCache(user.id, selectedCampaignId);
          
          if (cachedResult && mountedRef.current) {
            setTopAds(cachedResult.data as AdItem[]);
            setLastFetchTime(now);
            setIsLoading(false);
            fetchInProgress.current = false;
            return;
          }
        }
      }
      
      // Try to fetch real data if we have a token and campaign ID
      if (accessToken && selectedCampaignId) {
        const realData = await fetchRealAdData(accessToken, selectedCampaignId, user?.id);
        
        if (realData && mountedRef.current) {
          setTopAds(realData);
          setLastFetchTime(now);
          
          // Only show success toast if it's been more than 10 minutes since the last one
          if (force || now - lastSuccessToast.current > 10 * 60 * 1000) {
            toast({
              title: "Meta-Daten geladen",
              description: "Die Top-Werbeanzeigen wurden erfolgreich abgerufen.",
              variant: "default",
            });
            lastSuccessToast.current = now;
          }
          
          setIsLoading(false);
          fetchInProgress.current = false;
          return;
        }
      }
      
      // Fallback to sample data
      const adData = getFallbackAdData(user?.id, selectedCampaignId || 'default');
      
      if (mountedRef.current) {
        setTopAds(adData);
        setLastFetchTime(now);
      }
    } catch (error) {
      console.error("Error fetching top ads:", error);
      
      if (mountedRef.current) {
        setError("Fehler beim Laden der Top-Werbeanzeigen");
      }
      
      // Try to load from localStorage if everything else fails
      const localData = getLocalStorageAdData();
      if (localData && mountedRef.current) {
        setTopAds(localData);
      } else if (mountedRef.current) {
        // Last resort - generate fresh sample data
        setTopAds(getFallbackAdData(user?.id, selectedCampaignId || 'default'));
      }
    } finally {
      fetchInProgress.current = false;
      
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [accessToken, isConnected, selectedCampaignId, lastFetchTime, toast, user]);

  useEffect(() => {
    mountedRef.current = true;
    
    // Initial fetch
    fetchTopAds();
    
    // Setup refresh interval
    const intervalId = setInterval(() => {
      fetchTopAds();
    }, REFRESH_INTERVAL);
    
    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [fetchTopAds]);
  
  // When selectedCampaignId changes, refresh the data but with a slight delay
  useEffect(() => {
    if (selectedCampaignId) {
      // Add delay to prevent immediate API calls on campaign change
      const timeoutId = setTimeout(() => {
        console.log("Campaign changed, refreshing top ads");
        fetchTopAds(true);
      }, 1500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedCampaignId, fetchTopAds]);

  // Clean expired cache entries occasionally
  useEffect(() => {
    // Only run cache cleanup if user is authenticated
    if (!user || !user.id) return;
    
    // Clean expired cache entries every 24 hours
    const cleanupId = setInterval(cleanExpiredCache, 24 * 60 * 60 * 1000);
    
    return () => clearInterval(cleanupId);
  }, [user]);

  // Provide a refresh function for manual updates
  const refreshTopAds = useCallback(() => {
    const now = Date.now();
    // Only show toast if it's been more than 2 minutes since the last one
    if (now - lastSuccessToast.current > 2 * 60 * 1000) {
      toast({
        title: "Aktualisierung",
        description: "Top-Werbeanzeigen werden aktualisiert...",
        variant: "default",
      });
      lastSuccessToast.current = now;
    }
    
    fetchTopAds(true);
  }, [fetchTopAds, toast]);

  return { topAds, isLoading, error, refreshTopAds };
};

// Use export type for re-exporting types to fix the TS1205 error
export type { AdItem } from "./types";
